import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogField, DialogFooter, dialogInputStyle } from "@/components/ui/Dialog";
import { useCreateWorkspace } from "@/hooks/useCreateWorkspace";
import { ApiError } from "@/lib/api";
import { t } from "@/lib/strings";

function autoSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (workspaceId: string) => void;
}

export function CreateWorkspaceDialog({ open, onOpenChange, onCreated }: Props) {
  const nameId = useId();
  const slugId = useId();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mutation = useCreateWorkspace();

  useEffect(() => {
    if (!open) {
      setName("");
      setSlug("");
      setSlugDirty(false);
      setError(null);
      mutation.reset();
    }
    // mutation.reset is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !mutation.isPending;

  const effectiveSlug = slugDirty ? slug : autoSlug(name);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      const ws = await mutation.mutateAsync({
        name: trimmedName,
        slug: effectiveSlug || undefined,
      });
      onOpenChange(false);
      onCreated?.(ws.id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t.workspaceSlugTaken);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t.unknownError);
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t.createWorkspaceTitle}
      description={t.createWorkspaceDescription}
    >
      <form onSubmit={onSubmit}>
        <DialogField label={t.workspaceName} htmlFor={nameId} error={error ?? undefined}>
          <input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.workspaceNamePlaceholder}
            autoFocus
            maxLength={255}
            style={dialogInputStyle}
            data-testid="create-workspace-name"
          />
        </DialogField>

        <DialogField label={t.workspaceSlug} htmlFor={slugId} hint={t.workspaceSlugHint}>
          <input
            id={slugId}
            value={effectiveSlug}
            onChange={(e) => {
              setSlugDirty(true);
              setSlug(autoSlug(e.target.value));
            }}
            maxLength={64}
            style={dialogInputStyle}
            data-testid="create-workspace-slug"
          />
        </DialogField>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t.cancel}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={!canSubmit}
            data-testid="create-workspace-submit"
          >
            {mutation.isPending ? t.creating : t.create}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
